import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";
import { z } from "zod";

import { DateOfBirthField } from "@/shared/components";
import { GENDER, userAttributesSchema } from "@/shared/models";
import { dateOfBirthSchema } from "@/shared/models/legacy-schemas";
import { Form, RadioGroup, SectionHeader } from "@/shared/ui";
import { QuestionNavigation, QuestionStep } from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";
import { useQuestionnaireStore } from "../stores";

const schema = z.object({
  dateOfBirth: dateOfBirthSchema,
  gender: userAttributesSchema.shape.gender,
});

type PersonalInfoValues = z.infer<typeof schema>;

const GENDER_OPTIONS = [
  { value: GENDER.MALE, label: t`Male` },
  { value: GENDER.FEMALE, label: t`Female` },
] as const;

export const PersonalInfo = () => {
  const navigate = useNavigate();
  const initialWeight = useQuestionnaireStore((store) => store.initialWeight);
  const dateOfBirth = useQuestionnaireStore((store) => store.dateOfBirth);
  const gender = useQuestionnaireStore((store) => store.gender);
  const setDateOfBirth = useQuestionnaireStore((store) => store.setDateOfBirth);
  const setGender = useQuestionnaireStore((store) => store.setGender);

  const form = useForm<PersonalInfoValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: { dateOfBirth, gender },
  });

  if (initialWeight === undefined) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.MEASUREMENTS} replace />;
  }

  const handleNext = form.handleSubmit((values) => {
    setDateOfBirth(values.dateOfBirth);
    setGender(values.gender);
    navigate(QUESTIONNAIRE_ROUTE.INTAKE);
  });

  return (
    <Form.Provider {...form}>
      <QuestionStep
        onSubmit={handleNext}
        footer={
          <QuestionNavigation
            onBack={() => navigate(QUESTIONNAIRE_ROUTE.MEASUREMENTS)}
          />
        }
      >
        <SectionHeader title={t`Tell us a bit more about you`} />

        <div className="flex w-full flex-col gap-6 sm:max-w-xl">
          <DateOfBirthField
            className="mf-masked mf-listen"
            control={form.control}
            register={form.register}
          />

          <Form.Field
            control={form.control}
            name="gender"
            render={({ field: { value, onChange } }) => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label
                  aria-required
                >{t`Sex assigned at birth`}</Form.Label>
                <Form.Control>
                  <RadioGroup.Root
                    isCard
                    value={value ?? ""}
                    onValueChange={onChange}
                    className="sm:grid-cols-2"
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <RadioGroup.Item key={option.value} value={option.value}>
                        <RadioGroup.Label className="text-base">
                          {option.label}
                        </RadioGroup.Label>
                      </RadioGroup.Item>
                    ))}
                  </RadioGroup.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />
        </div>
      </QuestionStep>
    </Form.Provider>
  );
};
